// hooks/usePedidoMasivo.js
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery } from '../QueryContext';
import { useAuth } from '../AuthContext';

export const usePedidoMasivo = () => {
  const { getPedidosCatalogos, getProductosCliente, crearCotizacion, invalidateConfigCache } = useQuery();
  const { profile } = useAuth();

  // ✅ OBTENER EMAIL DEL PERFIL DEL USUARIO
  const emailUsuario = profile?.email || '';
  const nombreUsuario = profile?.nombre || ''; 
  
  // --- Estados del catálogo ---
  const [catalogData, setCatalogData] = useState(null);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(true);

  // --- Estados de selección ---
  const [selectedClients, setSelectedClients] = useState([]);
  const [orderLines, setOrderLines] = useState([]);
  const [productClientAssignment, setProductClientAssignment] = useState({});
  const [allProductosCliente, setAllProductosCliente] = useState([]);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [clientOCs, setClientOCs] = useState({});

  // ✅ Estado para forzar re-carga de productos cuando se pulsa "Refrescar"
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // ✅ Estado para comentario
  const [comentario, setComentario] = useState('');

  // --- Estados de guardado ---
  const [savingOrder, setSavingOrder] = useState(false);
  const [successModal, setSuccessModal] = useState({ 
    show: false, 
    message: '', 
    pedidos: [],
    skus_no_procesados: [],
    email_enviado: false,
    email_error: null
  });

  // --- Filtro de asesor ---
  const isAsesor = useMemo(() => {
    const cargos = ["ASESOR COMERCIAL DE VENTAS", "ASESOR COMERCIAL JUNIOR"];
    return profile?.cargo && cargos.includes(profile.cargo.toUpperCase());
  }, [profile]);
  const username = profile?.username || '';

  /* ================================================================
     CARGA DE CATÁLOGO (extraída para poder reutilizarla en recargar())
     ================================================================ */
  const cargarCatalogo = useCallback(async (force = false) => {
    setIsLoadingCatalog(true);
    try {
      if (force && invalidateConfigCache) {
        invalidateConfigCache('/pedidosapp');
      }
      const data = await getPedidosCatalogos(force);
      if (data?.success) setCatalogData(data);
    } catch (e) {
      console.error('Error catálogo:', e);
    } finally {
      setIsLoadingCatalog(false);
    }
  }, [getPedidosCatalogos, invalidateConfigCache]);

  useEffect(() => {
    cargarCatalogo();
  }, [cargarCatalogo]);

  /* ================================================================
     ✅ RECARGAR — fuerza recarga del catálogo Y de productos
     ================================================================ */
  const recargar = useCallback(async () => {
    console.log('[usePedidoMasivo] 🔄 Recargando catálogo y productos (forceRefresh=true)...');
    if (invalidateConfigCache) {
      invalidateConfigCache('/pedidosapp');
    }
    await cargarCatalogo(true);
    // Al cambiar este contador, se re-dispara el useEffect de productos
    setRefreshTrigger(t => t + 1);
    console.log('[usePedidoMasivo] ✅ Recarga solicitada');
  }, [cargarCatalogo, invalidateConfigCache]);

  /* ================================================================
     CARGA DE PRODUCTOS CUANDO CAMBIAN LOS CLIENTES SELECCIONADOS
     ================================================================ */
  useEffect(() => {
    if (selectedClients.length === 0) {
      setAllProductosCliente([]);
      return;
    }
    const fetchAllProductos = async () => {
      setLoadingProductos(true);
      try {
        const productsMap = {};
        const forzar = refreshTrigger > 0;
        for (const clientId of selectedClients) {
          const data = await getProductosCliente(clientId, forzar);
          if (data?.success) {
            data.productos.forEach(p => {
              const sku = p.sku;
              if (!productsMap[sku]) {
                productsMap[sku] = {
                  sku: p.sku, marca: p.marca, nombre: p.nombre,
                  uni_venta: p.uni_venta || 'UND', tipo_imp: p.tipo_imp || 1,
                  equivalencia: p.equivalencia || 1.0, 
                  stock_bl: p.stock_bl,
                  stock_disponible: p.stock_disponible || p.stock_bl || 0,
                  clientPrecios: {},
                };
              }
              const precioExistente = productsMap[sku].clientPrecios[clientId];
              if (!precioExistente || p.precio_bs > 0 || p.precio_usd > 0) {
                productsMap[sku].clientPrecios[clientId] = {
                  precio_bs: p.precio_bs || 0, precio_bs2: p.precio_bs2 || 0,
                  precio_usd: p.precio_usd || 0, codigo_vendedor: p.codigo_vendedor || '01',
                  cos_pro_un: p.cos_pro_un || 0, ult_cos_un: p.ult_cos_un || 0,
                  cos_pro_om: p.cos_pro_om || 0, ult_cos_om: p.ult_cos_om || 0
                };
              }
              if (p.stock_bl !== null && (productsMap[sku].stock_bl === null || p.stock_bl < productsMap[sku].stock_bl)) {
                productsMap[sku].stock_bl = p.stock_bl;
              }
            });
          }
        }
        setAllProductosCliente(Object.values(productsMap));
      } catch (err) { setAllProductosCliente([]); }
      finally { setLoadingProductos(false); }
    };
    fetchAllProductos();
  }, [selectedClients, getProductosCliente, refreshTrigger]);  // ✅ refreshTrigger en deps

  /* ================================================================
     OPCIONES DE CLIENTES - SIN DUPLICADOS
     ================================================================ */
  const clientesOptions = useMemo(() => {
    if (!catalogData?.clientes) return [];
    let clients = catalogData.clientes;
    
    const uniqueClients = new Map();
    clients.forEach(c => {
      if (!uniqueClients.has(c.id_cliente)) {
        uniqueClients.set(c.id_cliente, c);
      }
    });
    clients = Array.from(uniqueClients.values());
    
    if (isAsesor && username) {
      clients = clients.filter(c => {
        if (c.username_vendedor) return c.username_vendedor === username;
        if (c.codigo_vendedor) return c.codigo_vendedor === profile?.codigo_vendedor;
        return false;
      });
    }
    return clients
      .map(c => ({
        value: c.id_cliente,
        label: `${c.id_cliente} - ${c.cliente_principal} - ${c.cliente_nombre}`,
        nombre: c.cliente_nombre,
        porc_desc: parseFloat(c.porc_desc || 0),
        zona: c.zona || '',
        isExento: (c.zona || '') === '7',
        rif: c.rif || ''
      }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }, [catalogData, isAsesor, username, profile]);

  /* ================================================================
     FUNCIÓN AUXILIAR PARA OBTENER PRECIOS
     ================================================================ */
  const getClientPrecios = useCallback((sku, clientId) => {
    const line = orderLines.find(l => l.sku === sku);
    let precios = line?.clientPrecios?.[clientId];
    if (precios) return precios;
    const prod = allProductosCliente.find(p => p.sku === sku);
    return prod?.clientPrecios?.[clientId] || null;
  }, [orderLines, allProductosCliente]);

  /* ================================================================
     AGREGAR PRODUCTO
     ================================================================ */
  const handleAddProduct = useCallback((product) => {
    const sku = product.sku;
    setOrderLines(prev => {
      const exists = prev.find(l => l.sku === sku);
      if (!exists) {
        return [...prev, {
          sku: product.sku, marca: product.marca,
          nombre_producto: product.nombre,
          uni_venta: product.uni_venta || 'UND',
          tipo_imp: product.tipo_imp || 1,
          equivalencia: product.equivalencia || 1.0,
          stock_bl: product.stock_bl || 0,
          stock_disponible: product.stock_disponible || product.stock_bl || 0,
          clientPrecios: product.clientPrecios || {},
        }];
      }
      return prev;
    });
    setProductClientAssignment(prev => {
      const current = prev[sku] || {};
      const updated = { ...current };
      selectedClients.forEach(clientId => {
        const precios = product.clientPrecios?.[clientId];
        if (precios && (precios.precio_bs > 0 || precios.precio_usd > 0)) {
          if (!updated[clientId]) {
            updated[clientId] = { cantidad: 1, descuento: 0, descuento_pac: 0, descuento_final: 0 };
          }
        }
      });
      return { ...prev, [sku]: updated };
    });
  }, [selectedClients]);

  /* ================================================================
     HANDLERS DE EDICIÓN
     ================================================================ */
  const handleUpdateClientQuantity = useCallback((sku, clientId, cantidad) => {
    setProductClientAssignment(prev => ({
      ...prev,
      [sku]: { ...prev[sku], [clientId]: { ...prev[sku]?.[clientId], cantidad: Math.max(0, parseInt(cantidad) || 0) } }
    }));
  }, []);

  const handleUpdateClientDiscount = useCallback((sku, clientId, value) => {
    setProductClientAssignment(prev => ({
      ...prev,
      [sku]: { ...prev[sku], [clientId]: { ...prev[sku]?.[clientId], descuento: Math.min(100, Math.max(0, parseFloat(value) || 0)) } }
    }));
  }, []);

  const handleUpdateClientPacDiscount = useCallback((sku, clientId, value) => {
    setProductClientAssignment(prev => ({
      ...prev,
      [sku]: { ...prev[sku], [clientId]: { ...prev[sku]?.[clientId], descuento_pac: Math.min(100, Math.max(0, parseFloat(value) || 0)) } }
    }));
  }, []);

  const handleToggleProductClient = useCallback((sku, clientId) => {
    const precios = getClientPrecios(sku, clientId);
    if (!precios || (precios.precio_bs <= 0 && precios.precio_usd <= 0)) return;
    setProductClientAssignment(prev => {
      const current = prev[sku] || {};
      const updated = { ...current };
      if (updated[clientId]) {
        delete updated[clientId];
      } else {
        updated[clientId] = { cantidad: 1, descuento: 0, descuento_pac: 0, descuento_final: 0 };
      }
      return { ...prev, [sku]: updated };
    });
  }, [getClientPrecios]);

  const handleSelectAllClientsForProduct = useCallback((sku) => {
    setProductClientAssignment(prev => {
      const updated = {};
      selectedClients.forEach(clientId => {
        const precios = getClientPrecios(sku, clientId);
        if (precios && (precios.precio_bs > 0 || precios.precio_usd > 0)) {
          updated[clientId] = {
            cantidad: prev[sku]?.[clientId]?.cantidad || 1,
            descuento: prev[sku]?.[clientId]?.descuento || 0,
            descuento_pac: prev[sku]?.[clientId]?.descuento_pac || 0,
            descuento_final: 0
          };
        }
      });
      return { ...prev, [sku]: updated };
    });
  }, [selectedClients, getClientPrecios]);

  const handleRemoveProduct = useCallback((sku) => {
    setOrderLines(prev => prev.filter(l => l.sku !== sku));
    setProductClientAssignment(prev => {
      const newAssign = { ...prev };
      delete newAssign[sku];
      return newAssign;
    });
  }, []);

  /* ================================================================
     TOTALES POR CLIENTE
     ================================================================ */
  const clientTotals = useMemo(() => {
    const totals = {};
    selectedClients.forEach(clientId => {
      let totalBs = 0;
      let totalUSD = 0;
      orderLines.forEach(line => {
        const assign = productClientAssignment[line.sku]?.[clientId];
        if (assign && assign.cantidad > 0) {
          const precios = getClientPrecios(line.sku, clientId) || {};
          totalBs += assign.cantidad * (precios.precio_bs || 0);
          totalUSD += assign.cantidad * (precios.precio_usd || 0);
        }
      });
      totals[clientId] = { totalBs, totalUSD };
    });
    return totals;
  }, [selectedClients, orderLines, productClientAssignment, getClientPrecios]);

  const clientDescuentos = useMemo(() => {
    const map = {};
    selectedClients.forEach(id => {
      const client = clientesOptions.find(c => c.value === id);
      map[id] = parseFloat(client?.porc_desc || 0);
    });
    return map;
  }, [selectedClients, clientesOptions]);

  /* ================================================================
     GUARDAR PEDIDOS - CON COMENTARIO, EMAIL Y ARCHIVO OC
     ================================================================ */
  const handleSaveOrder = useCallback(async (ocFile = null, ocFileName = null) => {
    if (selectedClients.length === 0 || orderLines.length === 0) return;
    setSavingOrder(true);
    
    try {
      const todosPedidosCreados = [];
      const skusNoProcesados = [];
      let emailEnviadoGlobal = true;
      let emailErrorGlobal = null;
      
      for (const clientId of selectedClients) {
        const lineasCliente = [];
        let codigoVendedor = '01';
        
        for (const line of orderLines) {
          const precios = getClientPrecios(line.sku, clientId);
          if (precios && precios.codigo_vendedor) { 
            codigoVendedor = precios.codigo_vendedor; 
            break; 
          }
        }
        
        orderLines.forEach(line => {
          const assign = productClientAssignment[line.sku]?.[clientId];
          if (assign && assign.cantidad > 0) {
            const precios = getClientPrecios(line.sku, clientId) || {};
            
            const stockBl = line.stock_bl;
            const stockDisponible = line.stock_disponible || stockBl || 0;
            const tieneStock = stockDisponible > 0;
            
            console.log(`📦 ${line.sku}: stock_bl=${stockBl}, stock_disp=${stockDisponible}, tieneStock=${tieneStock}`);
            
            lineasCliente.push({
              sku: line.sku, 
              marca: line.marca, 
              nombre_producto: line.nombre_producto,
              bultos: assign.cantidad,
              precio_bs: precios.precio_bs || 0, 
              precio_bs2: precios.precio_bs2 || 0,
              precio_usd: precios.precio_usd || 0, 
              uni_venta: line.uni_venta,
              tipo_imp: line.tipo_imp, 
              equivalencia: line.equivalencia,
              cos_pro_un: precios.cos_pro_un || 0, 
              ult_cos_un: precios.ult_cos_un || 0,
              cos_pro_om: precios.cos_pro_om || 0, 
              ult_cos_om: precios.ult_cos_om || 0,
              descuento: assign.descuento || 0, 
              descuento_pac: assign.descuento_pac || 0,
              descuento_final: 0,
              stock_disponible: stockDisponible,
              tiene_stock: tieneStock
            });
          }
        });
        
        if (lineasCliente.length === 0) continue;
        
        const totalBsBruto = lineasCliente.reduce((acc, l) => acc + l.bultos * (l.precio_bs || 0), 0);
        const totalUSDBruto = lineasCliente.reduce((acc, l) => acc + l.bultos * (l.precio_usd || 0), 0);
        
        const descuentoCliente = clientDescuentos[clientId] || 0;

        const payload = {
          id_cliente: clientId,
          moneda_pago: 'USD',
          codigo_vendedor: codigoVendedor,
          lineas: lineasCliente,
          total_bs: totalBsBruto,
          total_usd: totalUSDBruto,
          descuento_cliente: descuentoCliente,
          usuario: profile?.username || 'SYSTEM',
          usuario_nombre: nombreUsuario,
          usuario_email: emailUsuario,
          orden_compra: clientOCs[clientId] || 'OC - ',
          comentario: comentario
        };
        
        console.log('📤 Enviando payload:', {
          cliente: clientId,
          lineas: lineasCliente.length,
          oc: clientOCs[clientId],
          archivo_oc: ocFile ? ocFileName : 'No adjuntado'
        });
        
        const result = await crearCotizacion(payload, ocFile, ocFileName);
        
        if (result.success) {
          if (result.email_enviado === false) {
            emailEnviadoGlobal = false;
            emailErrorGlobal = result.email_error || 'Error desconocido';
          }
          
          const descCliente = clientDescuentos[clientId] || 0;
          const totalConDescuento = totalUSDBruto * (1 - descCliente / 100);
          const factNums = result.fact_nums || [result.fact_num];
          
          const itemsPorLote = result.items_por_lote || [];
          factNums.forEach((factNum, idx) => {
            todosPedidosCreados.push({
              clienteId: clientId,
              factNum: factNum,
              total: totalConDescuento,
              moneda: 'USD',
              lineas: itemsPorLote[idx] || lineasCliente.length,
              lote: result.lotes > 1 ? `${idx + 1}/${result.lotes}` : null,
              email_enviado: result.email_enviado !== false,
              email_error: result.email_error || null
            });
          });

          if (result.skus_no_procesados && result.skus_no_procesados.length > 0) {
            result.skus_no_procesados.forEach(sku => {
              if (!skusNoProcesados.includes(sku)) {
                skusNoProcesados.push(sku);
              }
            });
          }
        }
      }
      
      setSuccessModal({ 
        show: true, 
        message: todosPedidosCreados.length > 0 
          ? `${todosPedidosCreados.length} pedido(s) creado(s) exitosamente` 
          : 'No se pudo crear ningún pedido.',
        pedidos: todosPedidosCreados,
        skus_no_procesados: skusNoProcesados,
        email_enviado: emailEnviadoGlobal,
        email_error: emailErrorGlobal
      });
    } catch (error) {
      console.error('Error completo:', error);
      alert(`Error: ${error.response?.data?.detail || error.message}`);
    } finally { 
      setSavingOrder(false); 
    }
  }, [selectedClients, orderLines, productClientAssignment, getClientPrecios, clientOCs, clientDescuentos, profile, emailUsuario, nombreUsuario, crearCotizacion, comentario]);

  const limpiarFormulario = useCallback(() => {
    setSelectedClients([]); setOrderLines([]); setProductClientAssignment({});
    setAllProductosCliente([]); setClientOCs({});
    setComentario('');
  }, []);

  const closeSuccessModal = useCallback(() => {
    setSuccessModal({ 
      show: false, 
      message: '', 
      pedidos: [], 
      skus_no_procesados: [],
      email_enviado: false,
      email_error: null
    });
    limpiarFormulario();
  }, [limpiarFormulario]);

  // --- Estados para Excel (compartidos) ---
  const [excelData, setExcelData] = useState([]);
  const [excelErrors, setExcelErrors] = useState([]);
  const [excelPreview, setExcelPreview] = useState([]);
  const [isProcessingExcel, setIsProcessingExcel] = useState(false);

  // --- Agregar productos desde Excel ---
  const addProductsFromExcel = useCallback(() => {
    if (excelData.length === 0 || selectedClients.length === 0) return;
    const newSkus = new Set();
    const newLines = [];
    const cleanCode = (code) => String(code).trim().replace(/^0+/, '').toUpperCase();
    const skuMap = {};
    allProductosCliente.forEach(p => {
      skuMap[cleanCode(p.sku)] = p.sku;
    });
    excelData.forEach(row => {
      const skuReal = skuMap[cleanCode(row.co_art)];
      const prod = allProductosCliente.find(p => p.sku === skuReal);
      if (prod && !newSkus.has(skuReal)) {
        newSkus.add(skuReal);
        newLines.push({
          sku: prod.sku, marca: prod.marca,
          nombre_producto: prod.nombre,
          uni_venta: prod.uni_venta || 'UND',
          tipo_imp: prod.tipo_imp || 1,
          equivalencia: prod.equivalencia || 1.0,
          stock_bl: prod.stock_bl || 0,
          stock_disponible: prod.stock_disponible || prod.stock_bl || 0,
          clientPrecios: prod.clientPrecios || {},
        });
      }
    });
    setOrderLines(prev => {
      const existingSkus = new Set(prev.map(l => l.sku));
      const toAdd = newLines.filter(l => !existingSkus.has(l.sku));
      return [...prev, ...toAdd];
    });
    setProductClientAssignment(prev => {
      const updated = { ...prev };
      excelData.forEach(row => {
        const skuReal = skuMap[cleanCode(row.co_art)];
        if (!skuReal) return;
        const clientId = row.co_cli;
        const prod = allProductosCliente.find(p => p.sku === skuReal);
        if (!prod) return;
        if (!selectedClients.includes(clientId)) return;
        if (!updated[skuReal]) updated[skuReal] = {};
        const precios = prod.clientPrecios?.[clientId];
        if (precios && (precios.precio_bs > 0 || precios.precio_usd > 0)) {
          updated[skuReal][clientId] = {
            cantidad: row.bultos,
            descuento: updated[skuReal][clientId]?.descuento || 0,
            descuento_pac: updated[skuReal][clientId]?.descuento_pac || 0,
            descuento_final: 0
          };
        }
      });
      return updated;
    });
  }, [excelData, selectedClients, allProductosCliente]);

  return {
    // Estados
    catalogData, isLoadingCatalog,
    selectedClients, setSelectedClients,
    orderLines, productClientAssignment,
    allProductosCliente, loadingProductos,
    clientOCs, setClientOCs,
    savingOrder, successModal, closeSuccessModal,
    comentario, setComentario,
    excelData, setExcelData,
    excelErrors, setExcelErrors,
    excelPreview, setExcelPreview,
    isProcessingExcel, setIsProcessingExcel,
    // Derivados
    isAsesor, username,
    clientesOptions, clientDescuentos, clientTotals,
    emailUsuario,
    nombreUsuario,
    // Funciones
    getClientPrecios,
    recargar,                    // ✅ NUEVA: fuerza recarga de catálogo + productos
    handleAddProduct,
    handleUpdateClientQuantity,
    handleUpdateClientDiscount,
    handleUpdateClientPacDiscount,
    handleToggleProductClient,
    handleSelectAllClientsForProduct,
    handleRemoveProduct,
    handleSaveOrder,
    limpiarFormulario,
    addProductsFromExcel,
  };
};